// 재택(PC간) 반출 기록 읽기 도우미 — HomeWorkBoard·ProductionBoard 공용 (2026-08-17)
// 기획서: docs/기획서_작업현황_반출표시_2026-08.md §2 A안
// agentTransfers(purpose 'home_work')의 반출(case)·반환(return)을 붙여 케이스 상태를 만든다.
// 조회는 화면이 하고, 이 파일은 순수 계산만 한다(파이어베이스 무의존).

const DAY = 24 * 3600 * 1000;

export const hwToDate = (t) => {
    if (!t) return null;
    if (t.toDate) return t.toDate();
    if (t instanceof Date) return t;
    const d = new Date(t);
    return isNaN(d) ? null : d;
};

const RET_LOST_MS = 7 * DAY;   // 반환 zip 서버 보관 상한 — 미배달 7일이면 서버가 지운다(stale_7d)

const hasIdx = (t) => t.folderIdx !== undefined && t.folderIdx !== null && Number.isInteger(Number(t.folderIdx));

/**
 * 반환 문서 1건이 "배달 못 받고 사라졌는가" — 에이전트 hasStalePurged(pipeline.js)와 같은 기준 +
 * 서버 정리 전이라도 7일이 지나도록 미배달이면 같은 취급(곧 서버가 지운다).
 * 배달 증거는 status 'delivered'만 인정(purgedAt은 미배달 삭제에도 찍힌다).
 */
export function isReturnLost(t, now = Date.now()) {
    if (!t || t.status === 'delivered') return false;
    if (t.purgedAt) return true;
    const at = hwToDate(t.createdAt);
    return !!at && now - at.getTime() > RET_LOST_MS;
}

/**
 * 반출 1건의 회신 판정 (2026-10-16 — 폴더별 짝짓기).
 * 회신 문서는 폴더(folderIdx)마다 따로 오가므로(에이전트 pipeline.js sendHomeReturn docExtra.folderIdx)
 * 케이스번호+폴더순번별 최신 rev를 모아 판정한다. folderIdx 없는 옛 문서만 있으면 종전처럼 케이스번호 기준.
 * @param {object} caseDoc 반출 문서(direction 'case')
 * @param {Array} rets 같은 caseNo의 반환 문서들
 * @returns {null | { status:'returned'|'returning'|'returnLost', done, total, lost, retAt, rev }}
 *          null = 회신 문서 없음
 */
export function judgeHomeReturn(caseDoc, rets, now = Date.now()) {
    const list = (rets || []).filter(Boolean);
    if (list.length === 0) return null;
    const timeOf = (r) => hwToDate(r.deliveredAt) || hwToDate(r.createdAt);
    const newest = (arr) => arr.reduce((a, b) => ((b.rev || 0) > (a.rev || 0) ? b : a));

    // 옛 문서(folderIdx 없음)만 있는 케이스 — 종전 판정(최신 rev 1건) 유지
    if (!list.some(hasIdx)) {
        const ret = newest(list);
        const delivered = ret.status === 'delivered';
        return { status: delivered ? 'returned' : 'returning', done: delivered ? 1 : 0, total: 1, lost: 0, retAt: timeOf(ret), rev: ret.rev || 0, legacy: true };
    }

    // 폴더순번별 최신 rev — folderIdx 없는 문서가 섞여 있으면 에이전트와 같이 0번 폴더로 본다
    const byIdx = new Map();
    for (const r of list) {
        const idx = hasIdx(r) ? Number(r.folderIdx) : 0;
        const cur = byIdx.get(idx);
        if (!cur || (r.rev || 0) > (cur.rev || 0)) byIdx.set(idx, r);
    }
    const latest = [...byIdx.values()];
    const done = latest.filter(r => r.status === 'delivered').length;
    const lost = latest.filter(r => isReturnLost(r, now)).length;
    // 폴더 수 = 반출 문서 folderCount (없는 옛 반출은 회신 온 폴더 수)
    const total = Math.max(Number(caseDoc?.folderCount) || 0, byIdx.size);
    const status = lost > 0 ? 'returnLost' : (done >= total ? 'returned' : 'returning');
    const times = latest.map(timeOf).filter(Boolean).map(d => d.getTime());
    return {
        status, done, total, lost,
        retAt: times.length ? new Date(Math.max(...times)) : null,
        rev: Math.max(...latest.map(r => r.rev || 0)),
        legacy: false,
    };
}

/** 회신 상태 짧은 문구 — 재택근무·제작 현황 공용. 회신 전 단계는 null */
export function homeReturnLabel(c) {
    if (!c) return null;
    if (c.status === 'returned') return '회신됨';
    if (c.status === 'returning') return c.retTotal > 1 ? `회신 중 (${c.retDone}/${c.retTotal})` : '회신 중';
    if (c.status === 'returnLost') return '회신 일부 못 받음';
    return null;
}
export const HOME_RETURN_LOST_HINT = '7일 안에 받지 못해 서버에서 지워졌습니다. 재택 PC에서 다시 회신해 주세요';

/**
 * 반출 1건(direction 'case')마다 폴더별 반환(return)을 붙여 상태를 만든다.
 * @param {Array} transfers agentTransfers 문서 배열({id, ...data})
 * @returns [{ id, caseNo, rawLabels[], folderCount, targetDeviceId, targetDeviceName,
 *             fromDeviceId, sentAt, gotAt, retAt, status('sent'|'designing'|'returning'|'returned'|'returnLost'),
 *             retDone, retTotal, retLost, rev, purged, stuck(보낸 지 하루 지나도 미도착), idle(도착 후 하루 무소식) }]
 */
export function joinHomeCases(transfers, now = Date.now()) {
    const returns = new Map();   // caseNo → 반환 문서들
    (transfers || []).filter(t => t.direction === 'return').forEach(t => {
        if (!returns.has(t.caseNo)) returns.set(t.caseNo, []);
        returns.get(t.caseNo).push(t);
    });
    return (transfers || [])
        .filter(t => t.direction === 'case')
        .map(t => {
            const j = judgeHomeReturn(t, returns.get(t.caseNo), now);
            const sentAt = hwToDate(t.createdAt);
            const gotAt = hwToDate(t.deliveredAt);
            const retAt = j ? j.retAt : null;
            const status = j ? j.status : (t.status === 'delivered' ? 'designing' : 'sent');
            return {
                id: t.id,
                caseNo: t.caseNo || '(번호 없음)',
                // patient 우선 (2026-09-01 대표 지시 "환자 이름이 나오게") — alpha.45부터 에이전트가 반출 시 환자명 추정을 동봉,
                // 구문서는 폴더명(label) 폴백 (HomeWorkBoard caseTitle이 다듬는다)
                rawLabels: Array.isArray(t.caseLabels) ? t.caseLabels.map(x => String(x?.patient || x?.label || '').trim()).filter(Boolean) : [],
                folderCount: t.folderCount || 0,
                targetDeviceId: t.targetDeviceId || '',
                targetDeviceName: t.targetDeviceName || '',
                fromDeviceId: t.fromDeviceId || '',
                sentAt, gotAt, retAt, status,
                retDone: j ? j.done : 0,
                retTotal: j ? j.total : 0,
                retLost: j ? j.lost : 0,
                rev: j ? j.rev : 0,
                purged: !!t.purgedAt,
                stuck: status === 'sent' && !!sentAt && now - sentAt.getTime() > DAY,
                idle: status === 'designing' && !!gotAt && now - gotAt.getTime() > DAY,
            };
        })
        .sort((a, b) => (b.sentAt?.getTime() || 0) - (a.sentAt?.getTime() || 0));
}

// 케이스 폴더명 '환자명_치과명' 분해 — 형식이 다르면(날짜 접두 등) 그대로 미매칭이 된다(안내줄이 받는다)
export const splitCaseLabel = (label) => {
    const t = String(label || '').trim();
    const i = t.indexOf('_');
    if (i <= 0) return { patient: t, clinic: '' };
    return { patient: t.slice(0, i), clinic: t.slice(i + 1) };
};

// PC 표시명 — 웹에서 바꾼 이름표(agentStatus.deviceNames, 웹 전용 필드)가 있으면 그것을 우선.
// 하트비트가 5분마다 devices.{id}.deviceName을 로컬값으로 덮어쓰므로, 이름표는 별도 맵에 둔다.
export const deviceLabelOf = (deviceNames, deviceId, fallback) =>
    (deviceNames && deviceId && deviceNames[deviceId]) || fallback || '알 수 없는 PC';
